import {
  applicationSubjectPropertyApi,
  applicationV2Api,
  loanApplicationsApi,
  loanAppraisalSurchargesApi,
  loanPropertyApi,
  postClosingApi,
} from 'app/services/api';
import _ from 'lodash';

import { LienPriorityType, LOAN_APPLICATION_ACTION_NAME_PREFIX } from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { checkIsChattelLoanPurpose } from 'utils/loanApplication';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { fetchChattelDetails, saveChattelPurposeAction } from './chattelDetails.actions';
import { getLoanApplicationHeaderViewAction } from './headerView.actions';
import { getLoanApplicationCommonDataByApplicationIdAction } from './loanApplication.actions';

export const PROPERTY_AND_LOAN_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/propertyAndLoan`,
  {
    GET_SUMMARY: 'GET_SUMMARY',
    GET_PROPERTY_AND_LOAN_EDIT_DATA: 'GET_PROPERTY_AND_LOAN_EDIT_DATA',
    GET_LOAN_AND_LOCK_EDIT_DATA: 'GET_LOAN_AND_LOCK_EDIT_DATA',
    EDIT_PROPERTY_AND_LOAN_DATA: 'EDIT_PROPERTY_AND_LOAN_DATA',
    EDIT_LOAN_AND_LOCK_DATA: 'EDIT_LOAN_AND_LOCK_DATA',
    GET_LOAN_APPRAISAL_SURCHARGES: 'GET_LOAN_APPRAISAL_SURCHARGES',
    SET_INVALID_TABS: 'SET_INVALID_TABS',
  },
);

export const getPropertyAndLoanSummaryAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanPropertyApi.getSubjectPropertySummaryView(params);

  return dispatch({
    type: PROPERTY_AND_LOAN_ACTION_TYPE.GET_SUMMARY,
    payload: data,
  });
});

export const getPropertyAndLoanSummaryEditDataAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await loanPropertyApi.getSubjectPropertyEditData(params);

    return dispatch({
      type: PROPERTY_AND_LOAN_ACTION_TYPE.GET_PROPERTY_AND_LOAN_EDIT_DATA,
      payload: data,
    });
  },
);

export const getLoanAndLockSummaryEditDataAction = catchAsyncActionError(
  ({ loanId, ...params }) =>
    async dispatch => {
      const [{ data }, { data: loanProgram }, { data: sellSideLock }] = await Promise.all([
        loanApplicationsApi.getLoanInfo(params),
        loanApplicationsApi.getLoanProgram(params).catch(() => ({ data: null })),
        loanId
          ? postClosingApi.getSellSideLock({ loanId }).catch(() => ({ data: null }))
          : { data: null },
      ]);

      // LEN-126: Loan Details edits the chattel purpose (ChattelDetails.PurposeOfChattel), so seed it
      // for a chattel loan only -- every other purpose skips the extra request.
      const isChattelLoan = checkIsChattelLoanPurpose(data.loan?.details?.purposeType?.type);
      const chattelDetails = isChattelLoan ? await fetchChattelDetails(params) : null;

      const formattedData = {
        ...data,
        loanProgram,
        sellSideLock,
        chattelDetails,
        additionalMortgages: _.orderBy(
          data.additionalMortgages,
          [
            ({ lienPriorityType }) => Object.values(LienPriorityType).indexOf(lienPriorityType),
            ({ amount }) => Number(amount),
          ],
          ['asc', 'desc'],
        ),
      };

      return dispatch({
        type: PROPERTY_AND_LOAN_ACTION_TYPE.GET_LOAN_AND_LOCK_EDIT_DATA,
        payload: formattedData,
      });
    },
);

export const editPropertyAndLoanSummaryAction = catchAsyncActionError(
  // loanId is part of every section-save param set but is not a subject-property field — strip it.
  ({ loanId, ...params }) =>
    async dispatch => {
      const { data } = await applicationSubjectPropertyApi.editSubjectProperty(params);

      dispatch({
        type: PROPERTY_AND_LOAN_ACTION_TYPE.EDIT_PROPERTY_AND_LOAN_DATA,
        payload: data,
      });

      return data;
    },
);

export const getLoanAppraisalSurchargesAction = catchAsyncActionError(
  ({ loanId, applicationId }) =>
    async dispatch => {
      const { data } = await loanAppraisalSurchargesApi.getLoanAppraisalSurcharges({
        loanId,
        applicationId,
      });

      return dispatch({
        type: PROPERTY_AND_LOAN_ACTION_TYPE.GET_LOAN_APPRAISAL_SURCHARGES,
        payload: data,
      });
    },
);

// purposeOfChattel is not part of the loan-info payload: it lives on the chattel-details row (null =
// nothing to save, the non-chattel/unchanged case). It is saved BEFORE the loan-info save through the
// purpose-only endpoint: that write is idempotent (and only ever read for a ChattelLoan), so a failure
// there leaves nothing half-applied, whereas failing after loan-info committed would make a retry
// resend loan-info's one-time creates (lock extensions, credits).
export const editLoanAndLockSummaryAction = catchAsyncActionError(
  ({ purposeOfChattel = null, ...params }) =>
    async dispatch => {
      if (!_.isNil(purposeOfChattel)) {
        await dispatch(
          saveChattelPurposeAction({ applicationId: params.applicationId, purposeOfChattel }),
        );
      }

      const { data } = await applicationV2Api.editLoanInfo(params);

      const result = dispatch({
        type: PROPERTY_AND_LOAN_ACTION_TYPE.EDIT_LOAN_AND_LOCK_DATA,
        payload: data,
      });

      dispatch(
        getLoanApplicationCommonDataByApplicationIdAction({ applicationId: params.applicationId }),
      );

      // LEN-205: the purpose-only endpoint is not a loan-editor save, so nothing else refreshes the header's
      // "Chattel Purchase" / "Chattel Refinance" (or the loan amount a chattel refinance re-derives from it).
      if (!_.isNil(purposeOfChattel)) {
        dispatch(getLoanApplicationHeaderViewAction({ applicationId: params.applicationId }));
      }

      return result;
    },
);

export const setInvalidTabsAction = data => ({
  type: PROPERTY_AND_LOAN_ACTION_TYPE.SET_INVALID_TABS,
  payload: data,
});
